import { FieldErrors, UseFormSetValue } from 'react-hook-form';
import { useMemo } from 'react';
import { OptionItem } from '../../../../types/components/common/select';
import { Tag } from '../../../Common/Tag';
import { GridContainer } from '../GridContainer';
import { RespondentChoice, RespondentFormData } from '../../../../types/sdq';
import { QuestionBlock } from '../QuestionBlock';
import { QuestionContainer } from '../QuestionContainer';
import { TextInputField } from '../../FormFields/TextInputField';
import { DatepickerField } from '../../FormFields/DatepickerField';
import { RadioGroupField } from '../../FormFields/RadioGroupField';
import { usesIdInput } from '../../../../constants/sdq';

interface Props {
  currentFormValues: RespondentFormData;
  setValue: UseFormSetValue<RespondentFormData>;
  errors: FieldErrors<RespondentFormData>;
  respondentChoice?: RespondentChoice;
  surveyTitle?: string;
}

export const Respondent = ({ setValue, errors, currentFormValues, respondentChoice, surveyTitle }: Props) => {
  const sexOptions: OptionItem[] = useMemo(() => {
    if (!respondentChoice) return [];
    return respondentChoice.sex;
  }, [respondentChoice]);

  const relationshipOptions: OptionItem[] = useMemo(() => {
    if (!respondentChoice) return [];
    return respondentChoice.relationship;
  }, [respondentChoice]);

  // 氏名の代わりにIDで識別する質問票かどうか
  const useIdInput = usesIdInput(surveyTitle);

  return (
    <QuestionBlock className={'rounded-lg bg-white sm:p-8 p-4 flex flex-col gap-8'}>
      <span className='font-semibold sm:text-xl text-lg'>基本情報記入</span>
      <GridContainer>
        {useIdInput ? (
          <QuestionContainer questionText={'お子さんID'}>
            <TextInputField
              value={currentFormValues.child_id}
              setValue={setValue}
              formErrors={errors}
              fieldKey={'child_id'}
              placeholder={'お子さんID'}
            />
          </QuestionContainer>
        ) : (
          <>
            <QuestionContainer questionText={'お子さんの姓'}>
              <TextInputField
                value={currentFormValues.last_name}
                setValue={setValue}
                formErrors={errors}
                fieldKey={'last_name'}
                placeholder={'姓'}
              />
            </QuestionContainer>
            <QuestionContainer questionText={'お子さんの名'}>
              <TextInputField
                value={currentFormValues.first_name}
                setValue={setValue}
                formErrors={errors}
                fieldKey={'first_name'}
                placeholder={'名'}
              />
            </QuestionContainer>
          </>
        )}
        {/* ID運用の質問票は画面にお誕生日・性別を表示しないため、送信データにも残らないよう非表示にする */}
        {!useIdInput && (
          <QuestionContainer questionText={'お子さんのお誕生日'}>
            <DatepickerField
              value={currentFormValues.birth_date}
              formErrors={errors}
              datepickerProps={{
                i18n: 'ja',
                asSingle: true,
                useRange: false,
                placeholder: '生年月日',
                inputClassName: `w-full bg-white rounded-md border py-2 px-5 text-black outline-none transition disabled:cursor-default disabled:bg-gray-2 disabled:border-gray-2`,
                maxDate: new Date(),
                readOnly: true
              }}
              setValue={setValue}
              fieldKey={'birth_date'}
            />
          </QuestionContainer>
        )}
        {!useIdInput && (
          <QuestionContainer questionText={'性別'}>
            <RadioGroupField
              setValue={setValue}
              value={currentFormValues.sex_of_child}
              formErrors={errors}
              fieldKey={'sex_of_child'}
              options={sexOptions}
            />
          </QuestionContainer>
        )}
      </GridContainer>

      <Tag>記入者の情報</Tag>
      <GridContainer>
        {/* ID運用の質問票は画面にお子さんとの関係を表示しないため、送信データにも残らないよう非表示にする */}
        {!useIdInput && (
          <QuestionContainer questionText={'お子さんとの関係'} required={true}>
            <RadioGroupField
              setValue={setValue}
              value={currentFormValues.relationship}
              formErrors={errors}
              fieldKey={'relationship'}
              options={relationshipOptions}
            />
          </QuestionContainer>
        )}

        {!useIdInput && currentFormValues.relationship === 'other' && (
          <QuestionContainer questionText={'その他の場合(具体的に)'} required={true}>
            <TextInputField
              value={currentFormValues.relationship_detail}
              setValue={setValue}
              formErrors={errors}
              fieldKey={'relationship_detail'}
              placeholder={'その他の場合'}
            />
          </QuestionContainer>
        )}
        {/*
          key を分けているのは、この 2 つが同じ位置・同じ型なので、付けないと React が
          DOM の input 要素を使い回してしまうため。TextInput は value ではなく
          defaultValue を渡す非制御コンポーネントで、defaultValue は要素の初回生成時にしか
          効かない。そのため要素が使い回されると、質問票を切り替えても前の質問票で入力した
          値が画面に残り続ける（フォームの値は空でも、記入者には残って見える）。
        */}
        {useIdInput ? (
          <QuestionContainer key={'teacher_id'} questionText={'保育士ID'}>
            <TextInputField
              value={currentFormValues.teacher_id}
              setValue={setValue}
              formErrors={errors}
              fieldKey={'teacher_id'}
              placeholder={'保育士ID'}
            />
          </QuestionContainer>
        ) : (
          <QuestionContainer key={'signature'} questionText={'記入者の署名'}>
            <TextInputField
              value={currentFormValues.signature}
              setValue={setValue}
              formErrors={errors}
              fieldKey={'signature'}
              placeholder={'署名'}
            />
          </QuestionContainer>
        )}
      </GridContainer>
    </QuestionBlock>
  );
};
